import { CSSTransition } from 'react-transition-group';
import { ImgHTMLAttributes, useMemo, useState } from 'react';
import { SerializedStyles, css } from '@emotion/react';
import { cache } from '@state/cache';
import { getImgProxySrc } from '@util/helper';
import ContentLoader from 'react-content-loader';
import DeadImagePlaceholder from '@img/placeholder.webp';
import styled from '@emotion/styled';

interface Props extends ImgHTMLAttributes<HTMLImageElement> {
  ratio?: number;
  extraStyle?: SerializedStyles;
  size?: number[];
}

/**
Image Proxy Enable:
- Load responsive image (src = null)
  - 200 -> onLoad -> update cache list status -> loading placeholder removed

    Image proxy default will return dead image placeholder if original image unreachable,
    so only image proxy down or processing image issue will return 400.
  - 400 -> onError -> load original image
    - 200 -> onLoad -> update currentSrc -> update cache list status -> remove loading placeholder
    - 400 -> onError -> load dead image placeholder
      - 200 -> onLoad -> update currentSrc -> update cache list status -> remove loading placeholder
      - 400 -> orError -> return

Image Proxy Disable:
- Load original image (srcSet = null)
  - 200 -> onLoad -> update currentSrc -> update cache list status -> remove loading placeholder
  - 400 -> onError -> load dead image placeholder
    - 200 -> onLoad -> update currentSrc -> update cache list status -> remove loading placeholder
    - 400 -> orError -> return
 */

function SkeletonImage(props: Props): JSX.Element {
  /* Define state / hook */
  const {
    height,
    width,
    src: originalSrc,
    alt,
    loading,
    className,
    ratio,
    extraStyle,
    size = [],
  } = props;
  const { cacheImgList, addCacheImg } = cache.useContainer();
  const [currentSrc, setCurrentSrc] = useState(originalSrc || '');

  /* Custom Data Object for render or other usages */
  const imgList = [originalSrc];

  /* Render control function / logic */
  const getSrcSet = useMemo(() => {
    if (cacheImgList[currentSrc] === false) return undefined;

    if (
      !process.env.REACT_APP_IMG_PROXY_URL ||
      size.length === 0 ||
      !originalSrc
    )
      return undefined;

    let srcSet = '';
    for (let i = 0; i < size.length; i += 1) {
      srcSet += `${getImgProxySrc(originalSrc, size[i])} ${size[i]}w, `;
    }
    return srcSet;
  }, [currentSrc, originalSrc, size]);

  const getSrc = useMemo(() => {
    /**
     * Prevent reload dead image again, if want to retry please comment it, but only for
     * image proxy disable
     */
    if (cacheImgList[String(originalSrc)] === false)
      return DeadImagePlaceholder;

    if (getSrcSet) return undefined; // Prevent running multiple onLoad
    return originalSrc;
  }, [getSrcSet, originalSrc]);

  /* Data Change Listeners - useEffect/useMemo/useCallback */

  /* Styling */
  const StyledImg = useMemo(
    () => styled.img`
      object-fit: cover;
      border-radius: 0.75rem;
      font-size: 0; // Hidden alternative text
      aspect-ratio: ${ratio};
      width: 100%;
      height: 100%;
      ${extraStyle}
    `,
    // Not adding 'ratio' and 'extraStyle' to prevent rerendering issue
    []
  );

  const Container = useMemo(
    () => styled.div`
      position: relative;
      display: flex;
      justify-content: center;
      ${height &&
      css`
        height: ${`${height}px`} !important;
      `}
      ${width &&
      css`
        width: ${`${width}px`} !important;
      `}
    `,
    [height, width]
  );

  /* Render */
  return (
    <Container className={className} slot={props.slot}>
      <StyledImg
        alt={alt}
        loading={loading || 'lazy'}
        src={getSrc}
        srcSet={getSrcSet}
        onError={(e) => {
          const { currentTarget } = e;
          const src = (e.target as HTMLImageElement).currentSrc;

          /**
           * Prevent infinite request if dead image placeholder also failed [Unexcepted]
           */
          if (src.endsWith(DeadImagePlaceholder)) return;

          if (src !== '') addCacheImg(src, false);

          if (currentTarget.srcset) {
            const orgImage = imgList[0];
            if (orgImage && cacheImgList[orgImage] !== false) {
              currentTarget.src = imgList.pop() || DeadImagePlaceholder;
            } else currentTarget.srcset = '';
          } else currentTarget.src = DeadImagePlaceholder;
        }}
        onLoad={(e) => {
          const src = (e.target as HTMLImageElement).currentSrc;
          /**
           * Default currentSrc is original image, we will update it if responsive image
           * or dead image placeholder is loaded
           */
          if (currentSrc !== src) setCurrentSrc(src);
          if (src !== '') addCacheImg(src);
        }}
      />
      <CSSTransition
        unmountOnExit
        classNames="content"
        in={cacheImgList[currentSrc] == null}
        timeout={0}
      >
        <ContentLoader
          backgroundColor="#d9d9d9"
          className="w-100 h-100 position-absolute"
          style={{ borderRadius: '0.75rem' }}
        >
          <rect height="100%" width="100%" />
        </ContentLoader>
      </CSSTransition>
    </Container>
  );
}

export default SkeletonImage;
